import React from 'react';
import { Calendar, ChevronDown } from 'lucide-react';
import { SmartDropdown } from '@/components/shared/SmartDropdown';
import { TimeFrame } from '@/types/dashboard';

interface TimeRangeSelectorProps {
    value: TimeFrame;
    onChange: (value: TimeFrame) => void;
    showHourOption?: boolean;
    className?: string;
}

interface RangeOption {
    id: TimeFrame;
    name: string;
}

const RANGE_OPTIONS: RangeOption[] = [
    { id: '1h', name: 'Last Hour' },
    { id: '24h', name: 'Last 24 Hours' },
    { id: '7d', name: 'Last 7 Days' },
    { id: '30d', name: 'Last 30 Days' },
    { id: '90d', name: 'Last 90 Days' },
];

export const TimeRangeSelector: React.FC<TimeRangeSelectorProps> = ({
    value,
    onChange,
    showHourOption = false,
    className = '',
}) => {
    const filteredOptions = showHourOption
        ? RANGE_OPTIONS
        : RANGE_OPTIONS.filter(opt => opt.id !== '1h');

    const selectedOption = RANGE_OPTIONS.find(opt => opt.id === value) || RANGE_OPTIONS[1];

    return (
        <div className={`flex flex-col gap-1.5 min-w-[200px] ${className}`}>
            <label className="text-[10px] font-bold text-neutral-400 uppercase tracking-[0.2em] px-1">
                Time Range
            </label>
            <SmartDropdown
                trigger={
                    <button
                        className="flex items-center justify-between w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl shadow-sm transition-all duration-200 hover:border-blue-400 hover:shadow-md group text-blue-600"
                    >
                        <div className="flex items-center gap-2.5">
                            <Calendar size={18} className="text-blue-500 group-hover:scale-110 transition-transform" />
                            <span className="text-sm font-semibold">{selectedOption.name}</span>
                        </div>
                        <ChevronDown size={16} className="text-gray-400" />
                    </button>
                }
                zIndex={10}
                minWidth="240px"
            >
                <div className="py-1">
                    {filteredOptions.map((option) => (
                        <div
                            key={option.id}
                            className={`px-4 py-3 text-sm cursor-pointer transition-colors flex items-center justify-between ${value === option.id
                                ? 'bg-blue-50 text-blue-700 font-bold'
                                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                                }`}
                            onClick={() => onChange(option.id)}
                        >
                            {option.name}
                            {value === option.id && (
                                <div className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                            )}
                        </div>
                    ))}
                </div>
            </SmartDropdown>
        </div>
    );
};